import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/services/chauffeur-services.png";
import { cityPriceFaq } from "./cityPricing";

const roquefortLesPins: ChauffeurPage = {
  slug: "roquefort-les-pins",
  title: "Chauffeur privé à Roquefort-les-Pins – VTC",
  subtitle:
    "Chauffeur privé et VTC à Roquefort-les-Pins : transfert aéroport de Nice, commune résidentielle et verdoyante entre Nice, Grasse et Valbonne.",
  keywords: [
    "chauffeur privé roquefort-les-pins",
    "vtc roquefort-les-pins",
    "chauffeur roquefort les pins",
    "transfert roquefort aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Roquefort-les-Pins",
    subtitle:
      "La commune résidentielle et verdoyante entre Nice, Grasse et Valbonne : transferts aéroport de Nice, mises à disposition et déplacements du quotidien.",
    keywords: [
      "VTC Roquefort-les-Pins",
      "chauffeur résidentiel",
      "transfert aéroport Nice",
      "chauffeur entre Nice et Valbonne",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à Roquefort-les-Pins",
    badges: [
      "Résidentiel verdoyant",
      "Entre Nice & Valbonne",
      "Chauffeurs bilingues",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Roquefort-les-Pins",
    items: [
      {
        title: "Commune résidentielle verdoyante",
        description:
          "Villas et domaines dispersés sous les pins : dépose au portail et chauffeur dédié pour les familles installées à Roquefort-les-Pins.",
      },
      {
        title: "Carrefour Nice–Grasse–Valbonne",
        description:
          "Position centrale entre le littoral, la capitale du parfum et la technopole : le chauffeur relie ces pôles sans souci de trajet.",
      },
      {
        title: "Aéroport de Nice ~35 min",
        description:
          "Rejoignez l’aéroport de Nice Côte d’Azur en environ 35 minutes par Villeneuve-Loubet et l’A8, avec suivi de vol.",
      },
      {
        title: "Chauffeurs bilingues",
        description:
          "FR/EN, basés sur la Côte d’Azur, ponctuels et discrets — 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Roquefort-les-Pins",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Prise en charge à Roquefort-les-Pins avec suivi de vol, vers et depuis l’aéroport de Nice Côte d’Azur, en environ 35 minutes.",
      },
      {
        title: "Mise à disposition résidentielle",
        text: "Un chauffeur dédié pour les propriétaires et les familles : rendez-vous, écoles, courses et sorties, avec dépose au portail.",
      },
      {
        title: "Liaison Sophia Antipolis & Valbonne",
        text: "Trajets vers la technopole et les entreprises voisines, pour rendez-vous d’affaires et déplacements professionnels.",
      },
      {
        title: "Navettes Nice, Cannes & Grasse",
        text: "Liaisons vers le littoral et l’arrière-pays, avec un chauffeur qui anticipe le trafic aux heures de pointe.",
      },
      {
        title: "Longue distance",
        text: "Trajets toute distance depuis Roquefort-les-Pins : Monaco, Saint-Tropez, Italie et stations de ski, en véhicule confortable.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Village de Roquefort-les-Pins",
        description: "Cœur de la commune, commerces et places.",
      },
      {
        name: "Domaines & villas sous les pins",
        description: "Quartiers résidentiels dispersés, dépose au portail.",
      },
      {
        name: "Le Rouret",
        description: "Village voisin, vers l’arrière-pays de Grasse.",
      },
      {
        name: "Villeneuve-Loubet",
        description: "Vers la mer et l’axe autoroutier de l’A8.",
      },
      {
        name: "Sophia Antipolis",
        description: "Technopole toute proche, entreprises et laboratoires.",
      },
      {
        name: "Aéroport de Nice",
        description: "À environ 35 minutes par Villeneuve-Loubet et l’A8.",
      },
    ],
  },
  generic: {
    heading:
      "Roquefort-les-Pins, résidentiel verdoyant au carrefour de l’ouest",
    subheading:
      "Une commune de villas sous les pins, idéalement placée entre Nice, Grasse et Valbonne.",
    paragraphs: [
      "Roquefort-les-Pins étire ses domaines et ses villas sous les pins, dans un cadre résidentiel et verdoyant apprécié des familles. Pour ceux qui y vivent, un chauffeur dédié simplifie le quotidien : rendez-vous, écoles, courses et sorties, avec une dépose au portail et sans souci de stationnement.",
      "La commune occupe un carrefour idéal entre le littoral niçois, la capitale du parfum et la technopole : Nice, Cannes, Grasse, Valbonne et Sophia Antipolis sont à portée de route. Nos chauffeurs anticipent le trafic aux heures de pointe pour tenir vos horaires.",
      "Pour l’aéroport de Nice Côte d’Azur, comptez environ 35 minutes par Villeneuve-Loubet et l’autoroute A8 selon le trafic ; nos chauffeurs bilingues suivent votre vol et adaptent le départ.",
    ],
    bullets: [
      "Dépose au portail",
      "Carrefour Nice–Grasse–Valbonne",
      "Suivi de vol & devis immédiat",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Roquefort-les-Pins",
    steps: [
      "Indiquez votre trajet (domicile, Sophia Antipolis, aéroport de Nice) et vos horaires.",
      "Nous confirmons le véhicule adapté et le forfait fixe ou la mise à disposition.",
      "Votre chauffeur suit votre vol et vous accueille au portail ou à l’adresse convenue.",
      "Ajustements en direct selon le trafic et votre programme.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Roquefort-les-Pins"),
    {
      q: "Combien de temps pour rejoindre l’aéroport de Nice ?",
      a: "Comptez environ 35 minutes par Villeneuve-Loubet et l’autoroute A8 selon le trafic, avec suivi de vol et accueil personnalisé.",
    },
    {
      q: "Proposez-vous un chauffeur dédié pour les familles résidentes ?",
      a: "Oui, pour les propriétaires et les familles installées à Roquefort-les-Pins, nous proposons une mise à disposition avec chauffeur dédié : rendez-vous, écoles, courses et sorties, avec dépose au portail.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "Réservez au plus tôt en haute saison et lors des événements de la région ; nous traitons aussi les demandes de dernière minute selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à une clientèle internationale.",
    },
    {
      q: "Quels véhicules proposez-vous ?",
      a: "Berline Mercedes Classe E et Classe S, van 7 places, SUV et minibus VIP, selon le nombre de passagers et l’occasion.",
    },
    {
      q: "Desservez-vous Sophia Antipolis et Valbonne depuis Roquefort-les-Pins ?",
      a: "Oui, la technopole et Valbonne sont à quelques minutes ; nous assurons transferts et mises à disposition pour vos rendez-vous d’affaires dans tout le secteur.",
    },
  ],
  relatedLinks: [
    {
      label: "Voiture avec chauffeur sur la Côte d’Azur",
      href: "/chauffeur/cote-d-azur",
    },
    {
      label: "Transferts depuis l’aéroport de Nice",
      href: "/chauffeur/aeroport",
    },
    { label: "Chauffeur privé Nice", href: "/chauffeur/nice" },
    { label: "Transferts avec chauffeur à Cannes", href: "/chauffeur/cannes" },
  ],
  cta: {
    title: "Votre chauffeur privé à Roquefort-les-Pins",
    text: "Transfert aéroport de Nice, mise à disposition ou liaison Sophia Antipolis — réponse rapide, 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé Roquefort-les-Pins — VTC & transfert",
    description:
      "Chauffeur privé et VTC à Roquefort-les-Pins : transfert aéroport de Nice en ~35 min, mise à disposition résidentielle, liaison Sophia Antipolis.",
    canonical: "/chauffeur/roquefort-les-pins",
  },
};

export default roquefortLesPins;
